import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Calculator, Info, Leaf, Plus, Sparkles, Trash2, TrendingUp, X as XIcon, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import LocalizedLink from "@/components/LocalizedLink";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";
import {
  COUNTRIES,
  calculateSavings,
  getCountry,
  type LocationInput,
  type MaintenanceLevel,
} from "@/lib/savings-model";

const newLocation = (countryCode = "IL"): LocationInput => ({
  id: Math.random().toString(36).slice(2),
  countryCode,
  region: getCountry(countryCode).regions[0].name,
  capacity: 10,
  sites: 1,
});

const money = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(value));

const number = (value: number, digits = 0) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: digits }).format(value);

/** Eases a number towards its target so results feel alive without being noisy. */
const useCountUp = (target: number) => {
  const [value, setValue] = useState(target);
  const frame = useRef<number>();

  useEffect(() => {
    const start = performance.now();
    const from = value;
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / 450);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(from + (target - from) * eased);
      if (progress < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return value;
};

const fieldClass =
  "w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground";

type SavingsCalculatorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const SavingsCalculator = ({ open, onOpenChange }: SavingsCalculatorProps) => {
  const { t } = useLanguage();
  const uid = useId();

  const [unit, setUnit] = useState<"kW" | "MW">("MW");
  const [locations, setLocations] = useState<LocationInput[]>([newLocation()]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [productionInput, setProductionInput] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [maintenance, setMaintenance] = useState<MaintenanceLevel>("reactive");
  const [implementation, setImplementation] = useState(60);

  const result = useMemo(
    () =>
      calculateSavings({
        unit,
        locations,
        annualProductionMwh: productionInput ? Number(productionInput) : undefined,
        pricePerKwh: priceInput ? Number(priceInput) : undefined,
        maintenance,
        implementation: implementation / 100,
      }),
    [unit, locations, productionInput, priceInput, maintenance, implementation],
  );

  const revenueLow = useCountUp(result.revenueLow);
  const revenueHigh = useCountUp(result.revenueHigh);
  const mwhLow = useCountUp(result.extraMwhLow);
  const mwhHigh = useCountUp(result.extraMwhHigh);

  const updateLocation = (id: string, patch: Partial<LocationInput>) =>
    setLocations((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  const maintenanceOptions: { value: MaintenanceLevel; label: string }[] = [
    { value: "none", label: t("No structured maintenance") },
    { value: "reactive", label: t("Reactive - we fix faults when reported") },
    { value: "scheduled", label: t("Scheduled preventive maintenance") },
    { value: "proactive", label: t("Data-driven proactive maintenance") },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto p-5 sm:p-7">
        <DialogHeader className="text-start">
          <DialogTitle className="text-2xl font-bold">
            {t("How much revenue could your solar portfolio be losing?")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Answer three quick questions for an instant estimate of the revenue WoozyAI could help recover. No registration needed.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Unit toggle */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-medium text-muted-foreground">
              {t("Total installed solar capacity")}
            </span>
            <div
              className="flex items-center gap-1 rounded-lg border border-border/60 bg-muted/20 p-1"
              role="group"
              aria-label={t("Capacity unit")}
            >
              {(["kW", "MW"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setUnit(option)}
                  aria-pressed={unit === option}
                  className={cn(
                    "rounded-md px-3 py-1 text-xs font-semibold transition-colors",
                    unit === option ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-primary",
                  )}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Locations */}
          <div className="space-y-3">
            {locations.map((location, index) => {
              const country = getCountry(location.countryCode);

              return (
                <div
                  key={location.id}
                  className="animate-fade-in rounded-xl border border-border/60 bg-card/60 p-4"
                >
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-1.5">
                      <Label htmlFor={`${uid}-country-${index}`} className="text-xs text-muted-foreground">
                        {t("Country")}
                      </Label>
                      <select
                        id={`${uid}-country-${index}`}
                        className={fieldClass}
                        value={location.countryCode}
                        onChange={(event) => {
                          const code = event.target.value;
                          updateLocation(location.id, {
                            countryCode: code,
                            region: getCountry(code).regions[0].name,
                          });
                        }}
                      >
                        {COUNTRIES.map((item) => (
                          <option key={item.code} value={item.code}>
                            {t(item.name)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor={`${uid}-region-${index}`} className="text-xs text-muted-foreground">
                        {t("Region")}
                      </Label>
                      <select
                        id={`${uid}-region-${index}`}
                        className={fieldClass}
                        value={location.region}
                        onChange={(event) => updateLocation(location.id, { region: event.target.value })}
                      >
                        {country.regions.map((region) => (
                          <option key={region.name} value={region.name}>
                            {t(region.name)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor={`${uid}-capacity-${index}`} className="text-xs text-muted-foreground">
                        {`${t("Capacity")} (${unit})`}
                      </Label>
                      <input
                        id={`${uid}-capacity-${index}`}
                        type="number"
                        min={0}
                        inputMode="decimal"
                        className={fieldClass}
                        value={location.capacity}
                        onChange={(event) =>
                          updateLocation(location.id, { capacity: Number(event.target.value) })
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor={`${uid}-sites-${index}`} className="text-xs text-muted-foreground">
                        {t("Number of sites")}
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          id={`${uid}-sites-${index}`}
                          type="number"
                          min={0}
                          inputMode="numeric"
                          className={fieldClass}
                          value={location.sites}
                          onChange={(event) =>
                            updateLocation(location.id, { sites: Number(event.target.value) })
                          }
                        />
                        {locations.length > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              setLocations((current) => current.filter((item) => item.id !== location.id))
                            }
                            className="rounded-md p-2 text-muted-foreground transition-colors hover:text-destructive"
                            aria-label={t("Remove this location")}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setLocations((current) => [...current, newLocation(current[0]?.countryCode)])}
            >
              <Plus className="me-2 h-4 w-4" aria-hidden="true" />
              {t("Add another location")}
            </Button>
          </div>

          {/* Optional refinement */}
          <div className="rounded-xl border border-border/60 bg-muted/10 p-4">
            <button
              type="button"
              onClick={() => setShowAdvanced((value) => !value)}
              aria-expanded={showAdvanced}
              className="flex w-full items-center justify-between gap-3 text-start"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                {t("Improve my estimate (optional)")}
              </span>
              <span className="text-xs text-muted-foreground">
                {showAdvanced ? t("Hide") : t("Show")}
              </span>
            </button>

            {showAdvanced && (
              <div className="animate-fade-in mt-4 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`${uid}-production`} className="text-xs text-muted-foreground">
                    {t("Annual electricity production (MWh per year)")}
                  </Label>
                  <input
                    id={`${uid}-production`}
                    type="number"
                    min={0}
                    inputMode="decimal"
                    className={fieldClass}
                    placeholder={number(result.baselineMwh)}
                    value={productionInput}
                    onChange={(event) => setProductionInput(event.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t("Default: modelled from your capacity and location.")}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`${uid}-price`} className="text-xs text-muted-foreground">
                    {t("Value of each kWh (USD)")}
                  </Label>
                  <input
                    id={`${uid}-price`}
                    type="number"
                    min={0}
                    step="0.001"
                    inputMode="decimal"
                    className={fieldClass}
                    placeholder={result.blendedPrice.toFixed(3)}
                    value={priceInput}
                    onChange={(event) => setPriceInput(event.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t("Editable default based on country average tariff or PPA price.")}
                  </p>
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`${uid}-maintenance`} className="text-xs text-muted-foreground">
                    {t("Current maintenance practice")}
                  </Label>
                  <select
                    id={`${uid}-maintenance`}
                    className={fieldClass}
                    value={maintenance}
                    onChange={(event) => setMaintenance(event.target.value as MaintenanceLevel)}
                  >
                    {maintenanceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor={`${uid}-implementation`} className="text-xs text-muted-foreground">
                      {t("Recommendation implementation level")}
                    </Label>
                    <span className="text-sm font-semibold text-primary">{implementation}%</span>
                  </div>
                  <Slider
                    id={`${uid}-implementation`}
                    min={10}
                    max={100}
                    step={5}
                    value={[implementation]}
                    onValueChange={([value]) => setImplementation(value)}
                    aria-label={t("Recommendation implementation level")}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t("Assumption: 60% of WoozyAI recommendations are implemented. Adjust freely.")}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="rounded-2xl border border-primary/25 bg-gradient-to-b from-primary/10 to-transparent p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("Estimated annual revenue recovery")}
            </p>
            <p className="mt-2 flex flex-wrap items-baseline gap-x-2 text-4xl font-bold text-foreground sm:text-5xl">
              <span>{money(revenueLow)}</span>
              <span className="text-muted-foreground">-</span>
              <span className="text-primary">{money(revenueHigh)}</span>
              <span className="text-sm font-medium text-muted-foreground">{t("per year")}</span>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("Before WoozyAI service fees and implementation costs.")}
            </p>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border/60 bg-card/60 p-3">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Zap className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  {t("Additional electricity production")}
                </p>
                <p className="mt-1 text-2xl font-semibold text-foreground">
                  {`${number(mwhLow)} - ${number(mwhHigh)} MWh`}
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card/60 p-3">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <TrendingUp className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  {t("Portfolio baseline production")}
                </p>
                <p className="mt-1 text-2xl font-semibold text-foreground">
                  {`${number(result.baselineMwh)} MWh`}
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card/60 p-3">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  {t("Portfolio size")}
                </p>
                <p className="mt-1 text-2xl font-semibold text-foreground">
                  {`${number(result.totalCapacityKw / 1000, 2)} MW · ${number(result.totalSites)} ${t("sites")}`}
                </p>
              </div>
            </div>
          </div>

          {/* Carbon, kept separate */}
          <div className="rounded-xl border border-border/60 bg-card/40 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Leaf className="h-4 w-4" style={{ color: "hsl(var(--recovery-green))" }} aria-hidden="true" />
              {t("Carbon credits and renewable energy certificates (separate)")}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {`${t("Estimated avoided emissions from the additional production:")} ${number(
                result.co2TonnesLow,
              )} - ${number(result.co2TonnesHigh)} tCO2e ${t("per year")}.`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t(
                "Any income from carbon credits or renewable energy certificates is excluded from the revenue figure above. It depends on eligibility, registry approval and verified market pricing.",
              )}
            </p>
          </div>

          {/* Assumptions */}
          <div className="rounded-xl border border-border/60 bg-muted/10 p-4">
            <p className="text-sm font-semibold text-foreground">{t("Assumptions behind this estimate")}</p>
            <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
              <li>
                {result.usedActualProduction
                  ? t("Baseline production: the annual figure you entered.")
                  : `${t("Location-specific yield used")}: ${number(result.blendedYield)} kWh/kWp ${t(
                      "per year",
                    )} (${t("editable default")}).`}
              </li>
              <li>
                {`${t("Value of energy")}: ${result.blendedPrice.toFixed(3)} USD/kWh (${t(
                  "editable default",
                )}).`}
              </li>
              <li>
                {`${t("Recoverable performance gap for the selected maintenance practice, scaled by your")} ${implementation}% ${t(
                  "implementation level",
                )}.`}
              </li>
              <li>{t("Figures are before WoozyAI service fees, hardware and implementation costs.")}</li>
              <li>
                {t(
                  "This is a directional estimate based on published industry averages, not a guarantee of savings.",
                )}
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-foreground">
              {t("Get a tailored assessment for your portfolio.")}
            </p>
            <Button variant="hero" asChild>
              <LocalizedLink to="/contact" onClick={() => onOpenChange(false)}>
                {t("Book a Demo")}
                <ArrowRight className="ms-2 h-4 w-4" aria-hidden="true" />
              </LocalizedLink>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CalculateSavingsButton = ({ className, fullWidth, iconOnly }: { className?: string; fullWidth?: boolean; iconOnly?: boolean }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size={iconOnly ? "icon" : fullWidth ? "default" : "sm"}
        className={cn("border-primary/40 text-primary hover:bg-primary/10", fullWidth && "w-full", className)}
        onClick={() => setOpen(true)}
        type="button"
        aria-label={iconOnly ? t("Calculate My Savings") : undefined}
        title={iconOnly ? t("Calculate My Savings") : undefined}
      >
        <Calculator className={cn("h-4 w-4", !iconOnly && "me-2")} aria-hidden="true" />
        {!iconOnly && t("Calculate My Savings")}
      </Button>
      <SavingsCalculator open={open} onOpenChange={setOpen} />
    </>
  );
};

/**
 * Compact inline calculator anchored flush beneath the header at the top of the
 * page. It scrolls away with the page; "Advanced Mode" opens the full dialog.
 */
const MiniSavingsCalculator = ({ mobile = false }: { mobile?: boolean }) => {
  const { t } = useLanguage();
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [unit, setUnit] = useState<"kW" | "MW">("MW");
  const [capacity, setCapacity] = useState(10);
  const [countryCode, setCountryCode] = useState("IL");
  const topOffset = 113;

  const result = useMemo(
    () =>
      calculateSavings({
        unit,
        locations: [
          {
            id: "mini",
            countryCode,
            region: getCountry(countryCode).regions[0].name,
            capacity,
            sites: 1,
          },
        ],
        maintenance: "reactive",
        implementation: 0.6,
      }),
    [unit, capacity, countryCode],
  );

  const revenueLow = useCountUp(result.revenueLow);
  const revenueHigh = useCountUp(result.revenueHigh);

  if (dismissed) return null;

  const calculator = (
    <>
      <div
        className={cn(
          mobile
            ? "relative z-10 mx-auto w-full max-w-md lg:hidden"
            : "absolute end-6 z-40 hidden w-80 lg:block motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-700",
        )}
        style={mobile ? undefined : { top: topOffset, maxHeight: `calc(100vh - ${topOffset + 24}px)` }}
      >
        <div className={cn(
          "relative h-full rounded-xl border border-primary/40 bg-background/95 shadow-[0_0_28px_rgba(0,240,255,0.28)]",
          mobile ? "p-5" : "overflow-y-auto overscroll-contain p-6",
        )}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2.5">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold leading-snug text-primary">
                {t("Calculate Potential Savings with WoozyAI")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="-me-1.5 -mt-1.5 shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:text-foreground"
              aria-label={t("Dismiss")}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-5 space-y-5">
            <div>
              <Label htmlFor={`${uid}-cap`} className="text-[13px] text-muted-foreground">
                {t("Total installed solar capacity")}
              </Label>
              <div className="mt-1.5 flex gap-1.5">
                <input
                  id={`${uid}-cap`}
                  type="number"
                  min={0}
                  inputMode="decimal"
                  value={capacity}
                  onChange={(event) => setCapacity(Math.max(0, Number(event.target.value)))}
                  className={cn(fieldClass, "min-w-0 flex-1 py-2.5 text-base")}
                />
                <div className="flex shrink-0 overflow-hidden rounded-lg border border-border">
                  {(["kW", "MW"] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setUnit(option)}
                      aria-pressed={unit === option}
                      className={cn(
                        "px-3 text-sm font-semibold",
                        unit === option ? "bg-primary/20 text-primary" : "text-muted-foreground",
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor={`${uid}-country`} className="text-[13px] text-muted-foreground">
                {t("Country")}
              </Label>
              <select
                id={`${uid}-country`}
                value={countryCode}
                onChange={(event) => setCountryCode(event.target.value)}
                className={cn(fieldClass, "mt-1.5 py-2.5 text-sm")}
              >
                {COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {t(country.name)}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {t("Estimated annual revenue recovery")}
              </p>
              <p
                className="mt-1.5 text-[28px] font-bold leading-tight text-primary"
                dir="ltr"
              >
                {`${money(revenueLow)} - ${money(revenueHigh)}`}
              </p>
            </div>

            <Button
              variant="outline"
              type="button"
              className="w-full border-primary/40 text-primary hover:bg-primary/10"
              onClick={() => setOpen(true)}
              aria-haspopup="dialog"
            >
              <TrendingUp className="me-2 h-4 w-4" aria-hidden="true" />
              {t("Advanced Mode")}
            </Button>

            <p className="text-[11px] italic leading-snug text-muted-foreground">
              {t(
                "Estimates are based on general assumptions and are non-binding; eligibility, savings, and revenue are not guaranteed.",
              )}
            </p>
          </div>
        </div>
      </div>
      <SavingsCalculator open={open} onOpenChange={setOpen} />
    </>
  );

  return mobile ? calculator : createPortal(calculator, document.body);
};

export const AdvancedModeCallout = () => <MiniSavingsCalculator />;

export const MobileSavingsCalculator = () => <MiniSavingsCalculator mobile />;

export default SavingsCalculator;
